import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
  Loader2,
  BookOpen,
  Circle,
  // MoreHorizontal, // disembunyikan sementara — dipakai oleh menu 3-titik & riwayat
  // History,
} from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import { RichTextEditor } from "@/components/ui/RichTextEditor";

// ── Types ─────────────────────────────────────────────────────────────────────

type JenisPnbp = { id: string; kode: string; nama: string; urutan: number };

type Cluster = {
  id: string;
  jenis_pnbp_id: string;
  title: string;
  deskripsi: string | null;
  content_html: string | null;
  status: string;
  urutan: number;
  is_active: boolean;
  published_at: string | null;
  updated_at: string | null;
};

type ClusterDraft = {
  title: string;
  deskripsi: string;
  content_html: string;
  is_active: boolean;
};

// ── Color palette (sama dengan PnbpKategoriPage) ──────────────────────────────

const COLORS = [
  { badge: "bg-emerald-600", bg: "bg-emerald-50",  border: "border-emerald-100", text: "text-emerald-700",  subtext: "text-emerald-600", dot: "bg-emerald-500"  },
  { badge: "bg-blue-600",    bg: "bg-blue-50",     border: "border-blue-100",    text: "text-blue-700",    subtext: "text-blue-600",    dot: "bg-blue-500"     },
  { badge: "bg-violet-600",  bg: "bg-violet-50",   border: "border-violet-100",  text: "text-violet-700",  subtext: "text-violet-600",  dot: "bg-violet-500"   },
  { badge: "bg-orange-500",  bg: "bg-orange-50",   border: "border-orange-100",  text: "text-orange-700",  subtext: "text-orange-600",  dot: "bg-orange-500"   },
  { badge: "bg-teal-600",    bg: "bg-teal-50",     border: "border-teal-100",    text: "text-teal-700",    subtext: "text-teal-600",    dot: "bg-teal-500"     },
  { badge: "bg-rose-600",    bg: "bg-rose-50",     border: "border-rose-100",    text: "text-rose-700",    subtext: "text-rose-600",    dot: "bg-rose-500"     },
];

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === "PUBLISHED")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
        <Circle className="h-2 w-2 fill-green-500 text-green-500" />
        Published
      </span>
    );
  if (status === "NONAKTIF")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
        <Circle className="h-2 w-2 fill-slate-400 text-slate-400" />
        Nonaktif
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
      <Circle className="h-2 w-2 fill-amber-500 text-amber-500" />
      Draft
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function StrukturPnbpEditPage() {
  const qc = useQueryClient();
  const [selectedJenisId, setSelectedJenisId] = useState<string>("");
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ClusterDraft>({
    title: "",
    deskripsi: "",
    content_html: "",
    is_active: true,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [jenisExpanded, setJenisExpanded] = useState(true);

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: jenisList = [] } = useQuery<JenisPnbp[]>({
    queryKey: ["master", "jenis-pnbp-legacy"],
    // PnbpCluster masih FK ke m_jenis_pnbp → pakai endpoint legacy (6 jenis asli).
    queryFn: () => api.get<JenisPnbp[]>("/master/jenis-pnbp-legacy"),
    staleTime: 10 * 60 * 1000,
  });

  const { data: clusters = [], isLoading: clustersLoading } = useQuery<Cluster[]>({
    queryKey: ["master", "pnbp-cluster", selectedJenisId],
    queryFn: () =>
      api.get<Cluster[]>("/master/pnbp-cluster", { jenis_pnbp_id: selectedJenisId }),
    enabled: !!selectedJenisId,
    staleTime: 0,
  });

  // Auto-select first jenis
  useEffect(() => {
    if (jenisList.length > 0 && !selectedJenisId) {
      setSelectedJenisId(jenisList[0].id);
    }
  }, [jenisList, selectedJenisId]);

  // Auto-select first cluster when clusters load
  useEffect(() => {
    if (clusters.length > 0 && !selectedClusterId) {
      setSelectedClusterId(clusters[0].id);
    }
    if (clusters.length === 0) setSelectedClusterId(null);
  }, [clusters]);

  // Load cluster into draft when selection changes
  const selectedCluster = clusters.find((c) => c.id === selectedClusterId) ?? null;
  useEffect(() => {
    if (selectedCluster) {
      setDraft({
        title: selectedCluster.title,
        deskripsi: selectedCluster.deskripsi ?? "",
        content_html: selectedCluster.content_html ?? "",
        is_active: selectedCluster.is_active,
      });
      setIsDirty(false);
    }
  }, [selectedClusterId]);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["master", "pnbp-cluster", selectedJenisId] });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const handleSaveDraft = async () => {
    if (!selectedClusterId) return;
    setSaving(true);
    try {
      await api.put(`/master/pnbp-cluster/${selectedClusterId}`, {
        title: draft.title,
        deskripsi: draft.deskripsi,
        content_html: draft.content_html,
        is_active: draft.is_active,
        status: "DRAFT",
      });
      await invalidate();
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedClusterId) return;
    setSaving(true);
    try {
      await api.put(`/master/pnbp-cluster/${selectedClusterId}`, {
        title: draft.title,
        deskripsi: draft.deskripsi,
        content_html: draft.content_html,
        is_active: draft.is_active,
      });
      await api.post(`/master/pnbp-cluster/${selectedClusterId}/publish`);
      await invalidate();
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSubsection = async () => {
    if (!selectedJenisId) return;
    setAddingNew(true);
    try {
      const newCluster = await api.post<Cluster>("/master/pnbp-cluster", {
        jenis_pnbp_id: selectedJenisId,
        title: "Subsection Baru",
        deskripsi: "",
        content_html: "",
        urutan: clusters.length,
        status: "DRAFT",
      });
      await invalidate();
      setSelectedClusterId(newCluster.id);
    } finally {
      setAddingNew(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/master/pnbp-cluster/${id}`),
    onSuccess: () => {
      setDeletingId(null);
      setSelectedClusterId(null);
      invalidate();
    },
  });

  const updateDraft = (patch: Partial<ClusterDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setIsDirty(true);
  };

  const selectedJenis = jenisList.find((j) => j.id === selectedJenisId);
  const jenisIdx = jenisList.findIndex((j) => j.id === selectedJenisId);
  const c = COLORS[jenisIdx % COLORS.length] ?? COLORS[0];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full -m-6">
      {/* ── Page title bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Edit Struktur PNBP</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Ubah dan kelola konten struktur PNBP sesuai kebutuhan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Disembunyikan sementara — bisa diaktifkan kembali bila diperlukan
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <History className="h-4 w-4" />
            Riwayat Perubahan
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-400 hover:bg-slate-50 transition-colors"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          */}
        </div>
      </div>

      {/* ── Action bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-6 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex-1 flex items-center gap-3">
          {selectedCluster && <StatusBadge status={selectedCluster.status} />}
          {selectedCluster?.updated_at && (
            <span className="text-xs text-slate-400">
              Terakhir diperbarui:{" "}
              {new Date(selectedCluster.updated_at).toLocaleString("id-ID", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Disembunyikan sementara — bisa diaktifkan kembali bila diperlukan
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Preview
          </button>
          */}
          <button
            type="button"
            disabled={!selectedCluster || saving}
            onClick={handleSaveDraft}
            className="rounded-lg border border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Simpan Draft"}
          </button>
          <button
            type="button"
            disabled={!selectedCluster || saving}
            onClick={handlePublish}
            className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: "#1B5189" }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Publish"}
          </button>
        </div>
      </div>

      {/* ── 3-column layout ────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT: two cards */}
        <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col overflow-y-auto gap-0">
          <div className="p-4 space-y-3">

            {/* ── Card 1: Pilih Jenis PNBP ───────────────────────── */}
            <div className="rounded-xl border-2 border-amber-300 bg-white p-4">
              <p className="text-sm font-bold text-slate-900 mb-3">
                1. Pilih Jenis PNBP (dari Master Data)
              </p>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Jenis PNBP <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={selectedJenisId}
                  onChange={(e) => {
                    setSelectedJenisId(e.target.value);
                    setSelectedClusterId(null);
                    setJenisExpanded(true);
                  }}
                  className="w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-9 text-sm text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {jenisList.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.kode} - {j.nama}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-slate-400" />
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <span className="flex-shrink-0 mt-0.5 h-4 w-4 rounded-full border-2 border-amber-500 flex items-center justify-center text-amber-600 text-[9px] font-bold">i</span>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Jenis PNBP diambil dari Master Data.
                  Perubahan jenis hanya dapat dilakukan
                  jika struktur belum memiliki konten.
                </p>
              </div>
            </div>

            {/* ── Card 2: Struktur & Subsection ──────────────────── */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {/* Card header */}
              <div className="px-4 pt-4 pb-3">
                <p className="text-sm font-bold text-slate-900">Struktur & Subsection</p>
                <p className="text-xs text-slate-400 mt-0.5">Kelola subsection pada kategori ini.</p>
              </div>

              {/* Jenis collapsible row */}
              <div className={clsx("mx-3 mb-2 rounded-lg overflow-hidden border", c.border)}>
                <button
                  type="button"
                  onClick={() => setJenisExpanded((v) => !v)}
                  className={clsx("flex w-full items-center gap-3 px-3 py-2.5", c.bg)}
                >
                  <div className={clsx("h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0", c.badge)}>
                    {jenisIdx + 1 || 1}
                  </div>
                  <span className="flex-1 text-left text-sm font-bold text-slate-800 truncate">
                    {selectedJenis?.nama ?? "—"}
                  </span>
                  {jenisExpanded
                    ? <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />}
                </button>

                {/* Subsection list */}
                {jenisExpanded && (
                  <div className="bg-white">
                    {clustersLoading ? (
                      <div className="flex justify-center py-5">
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      </div>
                    ) : clusters.length === 0 ? (
                      <p className="px-4 py-3 text-xs text-slate-400 italic">Belum ada subsection.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {clusters.map((cl, idx) => {
                          const isSelected = selectedClusterId === cl.id;
                          return (
                            <li key={cl.id}>
                              <div
                                onClick={() => { setSelectedClusterId(cl.id); }}
                                className={clsx(
                                  "group flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors",
                                  isSelected ? c.bg : "hover:bg-slate-50",
                                )}
                              >
                                <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0 cursor-grab" />
                                <span className={clsx(
                                  "text-xs font-semibold flex-shrink-0 w-8",
                                  isSelected ? c.subtext : "text-slate-400",
                                )}>
                                  {jenisIdx + 1}.{idx + 1}
                                </span>
                                <span className={clsx(
                                  "flex-1 text-sm truncate",
                                  isSelected ? `${c.text} font-semibold` : "text-slate-700",
                                )}>
                                  {cl.title}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setDeletingId(cl.id); }}
                                  className={clsx(
                                    "rounded p-0.5 text-slate-400 hover:text-red-600 flex-shrink-0 transition-opacity focus:opacity-100 group-hover:opacity-100",
                                    isSelected ? "opacity-100" : "opacity-0",
                                  )}
                                  title="Hapus subsection"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Tambah subsection */}
              <div className="px-3 pb-3">
                <button
                  type="button"
                  onClick={handleAddSubsection}
                  disabled={!selectedJenisId || addingNew}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-blue-300 py-2.5 text-sm font-semibold text-blue-600 hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-40"
                >
                  {addingNew ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Tambah Subsection
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* MIDDLE: Edit form */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {!selectedCluster ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
              <BookOpen className="h-12 w-12 opacity-20" />
              <p className="text-sm font-medium text-slate-500">
                Pilih subsection di kiri untuk mengedit konten.
              </p>
            </div>
          ) : (
            <div className="px-8 py-6 space-y-4">

              {/* Unsaved indicator */}
              {isDirty && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                  <Circle className="h-2 w-2 fill-amber-500 flex-shrink-0" />
                  Ada perubahan yang belum disimpan — klik <strong>Simpan Draft</strong> atau <strong>Publish</strong>.
                </div>
              )}

              {/* Form card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

                {/* Card header */}
                <div className="px-6 py-4 border-b border-slate-100 bg-white">
                  <h2 className="text-base font-bold text-slate-900">Konten Subsection</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Edit konten untuk subsection yang dipilih.
                  </p>
                </div>

                {/* Form body */}
                <div className="px-6 py-5 space-y-5">

                  {/* Title + Status */}
                  <div className="flex items-end gap-4">
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Judul Subsection <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={draft.title}
                        onChange={(e) => updateDraft({ title: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                        placeholder="Judul subsection..."
                      />
                    </div>
                    {/* Disembunyikan sementara — toggle Status bisa diaktifkan kembali bila diperlukan
                    <div className="flex-shrink-0 w-28">
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Status
                      </label>
                      <button
                        type="button"
                        onClick={() => updateDraft({ is_active: !draft.is_active })}
                        className="flex items-center gap-2 py-1.5"
                      >
                        <span
                          className={clsx(
                            "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                            draft.is_active ? "bg-green-500" : "bg-slate-200",
                          )}
                        >
                          <span
                            className={clsx(
                              "inline-block h-5 w-5 rounded-full bg-white shadow-lg transition-transform duration-200",
                              draft.is_active ? "translate-x-5" : "translate-x-0",
                            )}
                          />
                        </span>
                        <span
                          className={clsx(
                            "text-sm font-semibold",
                            draft.is_active ? "text-green-700" : "text-slate-500",
                          )}
                        >
                          {draft.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </button>
                    </div>
                    */}
                  </div>

                  <div className="border-t border-slate-100" />

                  {/* Deskripsi singkat */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-0.5">
                      Deskripsi Singkat
                    </label>
                    <p className="text-xs text-slate-400 mb-1.5">
                      Deskripsi singkat yang akan tampil pada landing page.
                    </p>
                    <input
                      type="text"
                      value={draft.deskripsi}
                      onChange={(e) => updateDraft({ deskripsi: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      placeholder="Deskripsi singkat subsection..."
                    />
                  </div>

                  <div className="border-t border-slate-100" />

                  {/* Konten Detail — Quill */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-0.5">
                      Konten Detail
                    </label>
                    <p className="text-xs text-slate-400 mb-1.5">
                      Isi konten lengkap untuk subsection ini.
                    </p>
                    <RichTextEditor
                      value={draft.content_html}
                      onChange={(v) => updateDraft({ content_html: v })}
                      placeholder="Tulis konten detail di sini..."
                      className="[&_.ql-editor]:min-h-[320px] [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:rounded-t-lg border border-slate-300 rounded-lg overflow-hidden"
                    />
                  </div>

                </div>
              </div>

            </div>
          )}
        </div>

      </div>

      {/* ── Delete confirm modal ───────────────────────────────────────────── */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-bold text-slate-900 mb-2">Hapus Subsection?</h3>
            <p className="text-sm text-slate-600 mb-5">
              Subsection ini akan dihapus permanen beserta seluruh kontennya.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeletingId(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deletingId)}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deleteMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
